/**
 * OpenTelemetry traces for @spatius/avatarkit-rtc SDK.
 *
 * Piggybacks on the global TracerProvider that @spatius/avatarkit registers via
 * `trace.setGlobalTracerProvider()`, exactly as otel-tracker.ts does for logs and
 * otel-metrics.ts for metrics. avatarkit owns the exporter and Resource, so spans
 * emitted here inherit app_id / region / dsm / sdk.version.
 *
 * ## Why this trace is client-only
 *
 * web-sdk drives the avatar itself: it mints the conversation_id, stamps a W3C
 * traceparent on the first audio chunk, and the server hangs `audio.process`
 * under the same root — one trace spanning client and server.
 *
 * The RTC SDK cannot do that. Microphone audio travels up through LiveKit/Agora
 * to the agent, which drives the avatar service on the host's behalf, and there
 * is no upstream channel on which to stamp a traceparent. The join therefore
 * runs the other way: the driving service stamps its own traceparent on the
 * animation, and `peekTraceparent` reads it back off the round's first packet
 * so the round is reported under the server's trace rather than one derived
 * from an id the server never saw.
 *
 * When the server sends none — an older one, or a path that does not stamp it —
 * the trace_id falls back to being derived from the locally minted
 * conversation_id, which still lets the trace be looked up from the matching
 * `rtc_playback_stats` event. That half covers exactly one thing: what this
 * client did with the frames it received.
 *
 * @packageDocumentation
 * @internal
 */

import { trace } from '@opentelemetry/api';
import type { Attributes } from '@opentelemetry/api';
import {
  startPlaybackTrace as avatarkitStartPlaybackTrace,
  peekTraceparent,
  type PlaybackTrace,
} from '@spatius/avatarkit/internal-telemetry';
/**
 * Kept as a no-op: spans are now built by the host SDK, which stamps its own
 * instrumentation-scope version, so there is no longer a version for this
 * module to hold. The setter stays so telemetry.ts keeps one shape for all
 * three channels.
 * @internal
 */
export function setTraceVersion(_version: string): void {}

/**
 * FNV-1a 32-bit with a variable seed, so one string yields several independent
 * hashes. Same implementation web-sdk uses, so an id maps to the same trace_id
 * on both sides.
 * @internal
 */
function fnv1a(str: string, seed: number): number {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Map a conversation id onto a valid W3C trace_id (16 bytes / 32 hex).
 *
 * A conversation id (`20260718110509_4xz9iyH8F3Gi`) is not a legal trace_id, so
 * four differently seeded FNV-1a hashes are concatenated into 128 bits. This is
 * deterministic: the same id always yields the same trace_id, so the id carried
 * on the playback event is enough to find the trace.
 * @internal
 */
export function conversationIdToTraceId(conversationId: string): string {
  const seeds = [0x811c9dc5, 0x1000193, 0xdeadbeef, 0x9e3779b9];
  let hex = '';
  for (const s of seeds) {
    hex += fnv1a(conversationId, s).toString(16).padStart(8, '0');
  }
  // trace_id must not be all zeroes (invalid per W3C).
  return /^0+$/.test(hex) ? '0'.repeat(31) + '1' : hex;
}

/**
 * Force-export any buffered spans.
 *
 * The global provider is avatarkit's, so `forceFlush` is not guaranteed to be
 * on the API surface and is called defensively. The global registry also hands
 * back a proxy — the real provider owning the span processor sits behind
 * `getDelegate()`.
 * @internal
 */
export function flushTraces(): void {
  try {
    type FlushableProvider = {
      forceFlush?: () => Promise<void>;
      getDelegate?: () => FlushableProvider;
    };
    const registered = trace.getTracerProvider() as FlushableProvider;
    const provider = registered.getDelegate?.() ?? registered;
    provider.forceFlush?.().catch(() => {});
  } catch {
    // ignore
  }
}

/**
 * A playback trace for one round.
 *
 * Re-exported from avatarkit rather than defined here: the tree shape and the
 * id derivation are a wire contract shared with the backend and the other two
 * platform SDKs, and a second definition of it would drift the first time
 * either side changed.
 * @internal
 */
export type { PlaybackTrace };

/**
 * Open one round's trace: `driven.request` → `playback` → the caller's spans.
 *
 * Delegates to avatarkit so both packages emit the same tree. This used to be
 * reimplemented here — avatarkit exposed no equivalent — which is why RTC
 * rounds arrived under a `rtc.playback` root while avatarkit's own arrived
 * under `driven.request`, and the two could not be read together.
 * @internal
 */
export function startPlaybackTrace(
  conversationId: string,
  startTimeMs?: number,
  attrs: Attributes = {},
  serverTraceparent?: string,
): PlaybackTrace {
  return avatarkitStartPlaybackTrace(
    conversationId,
    startTimeMs,
    attrs,
    serverTraceparent,
  );
}

/**
 * Re-exported from avatarkit so the round's trace can be anchored on the one
 * the driving service stamped on the animation — see its own docs.
 * @internal
 */
export { peekTraceparent };
